import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { StatusChip } from "@/components/common/StatusChip";
import { InlineNotice } from "@/components/common/InlineNotice";
import { BadgeList } from "@/components/common/Badges";
import {
  Upload, LogOut, Star, MapPin, CheckCircle2, ShieldCheck, Sparkles,
} from "lucide-react";
import {
  EXPERIENCE_LEVELS, YEARS_EXPERIENCE, FITNESS_LEVELS, TRAINING_PACE,
  SERVICES, AVAILABILITY_SLOTS, TRANSPORT_METHODS, ENCOURAGEMENT,
  BUILTIN_SPORTS, COMMON_POSITIONS,
} from "@/lib/ballboyOptions";

function toggle(list, v) {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

export default function ProfilePage() {
  const { user, setUser, logout } = useAuth();
  const isBB = user?.role === "ballboy";

  // Common fields
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [photo, setPhoto] = useState(user?.photo || null);
  const [sports, setSports] = useState(user?.sports || []);
  const [customSport, setCustomSport] = useState("");

  // Ball-boy fields
  const [hourly, setHourly] = useState(user?.hourly_rate ?? 20);
  const [experienceLevel, setExperienceLevel] = useState(user?.experience_level || "");
  const [sportExperience, setSportExperience] = useState(user?.sport_experience || {});
  const [positions, setPositions] = useState(user?.positions_played || []);
  const [customPosition, setCustomPosition] = useState("");
  const [services, setServices] = useState(user?.services || []);
  const [years, setYears] = useState(user?.years_experience || "");
  const [fitness, setFitness] = useState(user?.fitness_level || "");
  const [pace, setPace] = useState(user?.training_pace || "");
  const [availability, setAvailability] = useState(user?.typical_availability || []);
  const [transport, setTransport] = useState(user?.transportation || []);
  const [encouragement, setEncouragement] = useState(user?.encouragement_style || "");
  const [canCount, setCanCount] = useState(!!user?.can_count_stats);
  const [bgCheck, setBgCheck] = useState(!!user?.background_check_consent);
  const [ec, setEc] = useState(user?.emergency_contact || { name: "", phone: "", relationship: "" });

  const [idPhoto, setIdPhoto] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [verif, setVerif] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isBB) api.get("/ballboy/verification").then((r) => setVerif(r.data)).catch(() => {});
  }, [isBB]);

  const positionOptions = useMemo(() => {
    const set = new Set();
    sports.forEach((s) => (COMMON_POSITIONS[s] || []).forEach((p) => set.add(p)));
    positions.forEach((p) => set.add(p));
    return Array.from(set);
  }, [sports, positions]);

  async function readFile(file) {
    return new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(file);
    });
  }

  function addCustomSport() {
    const v = customSport.trim().toLowerCase();
    if (!v) return;
    if (!sports.includes(v)) setSports([...sports, v]);
    setCustomSport("");
  }

  function addCustomPosition() {
    const v = customPosition.trim().toLowerCase();
    if (!v) return;
    if (!positions.includes(v)) setPositions([...positions, v]);
    setCustomPosition("");
  }

  async function save() {
    setSaving(true);
    try {
      const body = { name, bio, sports, photo };
      if (isBB) {
        Object.assign(body, {
          hourly_rate: parseFloat(hourly) || 0,
          experience_level: experienceLevel || null,
          sport_experience: sportExperience,
          positions_played: positions,
          services,
          years_experience: years || null,
          fitness_level: fitness || null,
          training_pace: pace || null,
          typical_availability: availability,
          transportation: transport,
          encouragement_style: encouragement || null,
          can_count_stats: canCount,
          background_check_consent: bgCheck,
          emergency_contact: ec,
        });
      }
      const { data } = await api.patch("/profile", body);
      setUser(data.user);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function uploadId() {
    if (!idPhoto) return toast.error("Please choose a photo of your ID first.");
    if (!selfie) return toast.error("Please add a selfie so we can verify it matches your ID.");
    setVerifying(true);
    try {
      const { data } = await api.post("/ballboy/id-upload", { id_photo: idPhoto, selfie });
      const status = data?.verification_status;
      const sim = data?.similarity;
      const idFlags = data?.id_check?.flags || [];
      if (status === "approved") {
        toast.success(`Verified automatically${sim != null ? ` (${sim}% face match)` : ""}`);
      } else if (status === "rejected") {
        const reason = idFlags.includes("no_face_on_id")
          ? "We couldn't find a face on your ID — please upload a clearer photo of a government ID."
          : sim != null && sim < 90
            ? `Face match only ${sim}% — please retake clearer photos.`
            : "Please retake clearer photos and try again.";
        toast.error("Verification failed", { description: reason });
      } else {
        toast.success("Submitted — admin will review shortly", {
          description: sim != null ? `Face match: ${sim}%` : undefined,
        });
      }
      setIdPhoto(null);
      setSelfie(null);
      const v = await api.get("/ballboy/verification");
      setVerif(v.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setVerifying(false);
    }
  }

  async function setLocation() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { data } = await api.patch("/profile", {
        lat: pos.coords.latitude, lng: pos.coords.longitude,
      });
      setUser(data.user);
      toast.success("Location saved");
    });
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Identity */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-14 w-14">
              {photo && <AvatarImage src={photo} />}
              <AvatarFallback>{(user?.name || "?")[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-semibold bb-display truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
              <div className="text-xs flex items-center gap-1 mt-1">
                <Star className="h-3 w-3" /> {user?.rating_avg?.toFixed?.(1) || "0.0"} ({user?.rating_count || 0})
              </div>
            </div>
            <label className="text-xs text-primary underline cursor-pointer">
              Change
              <input type="file" accept="image/*" hidden onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                setPhoto(await readFile(f));
              }} data-testid="profile-photo-input" />
            </label>
          </div>
          {isBB && user?.badges?.length > 0 && (
            <div className="mt-3">
              <BadgeList badges={user.badges} max={99} />
            </div>
          )}
        </CardContent>
      </Card>

      {isBB && user && !user.profile_completed && (
        <InlineNotice tone="warning" title="Complete your profile">
          Athletes use your profile info to pick a ball boy. Finish the sections
          below to unlock accepting jobs.
        </InlineNotice>
      )}

      {!isBB && (
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground">Trust level</div>
              <div className="text-xl font-semibold bb-display">
                {user?.trust_level === 2 ? "Verified Athlete" : user?.trust_level === 1 ? "Basic (Level 1)" : "Unverified"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Private / late-night / high-value bookings require Level 2.
              </div>
            </div>
            <Button variant="secondary" onClick={() => window.location.assign("/athlete/verify")} data-testid="profile-trust-link">Verify</Button>
          </CardContent>
        </Card>
      )}

      {/* Basics */}
      <Card>
        <CardHeader><CardTitle className="bb-display">Basics</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </Field>
          <Field label="Bio">
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder={isBB ? "Tell athletes what makes you great to practice with." : "Say hi!"}
              data-testid="profile-bio-input"
            />
          </Field>
          <Field label={isBB ? "Sports you can assist with" : "Preferred sports"}>
            <div className="flex flex-wrap gap-2">
              {Array.from(new Set([...BUILTIN_SPORTS, ...sports])).map((s) => (
                <Chip key={s} active={sports.includes(s)} onClick={() => setSports(toggle(sports, s))} testId={`profile-sport-${s}`}>
                  {s}
                </Chip>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Input value={customSport} onChange={(e) => setCustomSport(e.target.value)} placeholder="Add other (e.g. pickleball)" data-testid="profile-custom-sport-input"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomSport(); } }} />
              <Button type="button" variant="secondary" onClick={addCustomSport} data-testid="profile-add-custom-sport">Add</Button>
            </div>
          </Field>
          {isBB && (
            <Field label="Hourly rate ($)">
              <Input type="number" value={hourly} onChange={(e) => setHourly(e.target.value)} data-testid="profile-rate-input" />
            </Field>
          )}
        </CardContent>
      </Card>

      {isBB && (
        <>
          {/* Experience */}
          <Card>
            <CardHeader>
              <CardTitle className="bb-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Experience</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Field label="Experience level per sport">
                {sports.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Pick sports above to set per-sport experience.</p>
                ) : (
                  <div className="space-y-2">
                    {sports.map((s) => (
                      <div key={s} className="flex items-center gap-2">
                        <div className="text-sm capitalize w-28 truncate">{s}</div>
                        <div className="flex-1">
                          <Select
                            value={sportExperience[s] || ""}
                            onValueChange={(v) => setSportExperience({ ...sportExperience, [s]: v })}
                          >
                            <SelectTrigger data-testid={`profile-sport-experience-${s}`}>
                              <SelectValue placeholder="Select experience" />
                            </SelectTrigger>
                            <SelectContent>
                              {EXPERIENCE_LEVELS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Field>
              <Field label="Positions played (team sports)">
                <div className="flex flex-wrap gap-2">
                  {positionOptions.map((p) => (
                    <Chip key={p} active={positions.includes(p)} onClick={() => setPositions(toggle(positions, p))} testId={`profile-position-${p.replace(/\s+/g, "-")}`}>{p}</Chip>
                  ))}
                </div>
                <div className="flex gap-2 pt-2">
                  <Input value={customPosition} onChange={(e) => setCustomPosition(e.target.value)} placeholder="Add position"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomPosition(); } }}
                    data-testid="profile-custom-position-input" />
                  <Button type="button" variant="secondary" onClick={addCustomPosition}>Add</Button>
                </div>
              </Field>
              <Field label="Years of sports experience">
                <Select value={years} onValueChange={setYears}>
                  <SelectTrigger data-testid="profile-years"><SelectValue placeholder="Select years" /></SelectTrigger>
                  <SelectContent>
                    {YEARS_EXPERIENCE.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </CardContent>
          </Card>

          {/* Services */}
          <Card>
            <CardHeader><CardTitle className="bb-display">What can you assist with?</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <Chip key={s} active={services.includes(s)} onClick={() => setServices(toggle(services, s))} testId={`profile-service-${s.replace(/[^a-z0-9]+/gi, "-")}`}>{s}</Chip>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Fitness */}
          <Card>
            <CardHeader><CardTitle className="bb-display">Fitness & pace</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Fitness level">
                <Select value={fitness} onValueChange={setFitness}>
                  <SelectTrigger data-testid="profile-fitness"><SelectValue placeholder="Select fitness" /></SelectTrigger>
                  <SelectContent>
                    {FITNESS_LEVELS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Can you keep up with advanced training sessions?">
                <Select value={pace} onValueChange={setPace}>
                  <SelectTrigger data-testid="profile-pace"><SelectValue placeholder="Select pace" /></SelectTrigger>
                  <SelectContent>
                    {TRAINING_PACE.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </CardContent>
          </Card>

          {/* Availability & Transport */}
          <Card>
            <CardHeader><CardTitle className="bb-display">Availability</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Typical availability">
                <div className="flex flex-wrap gap-2">
                  {AVAILABILITY_SLOTS.map((s) => (
                    <Chip key={s} active={availability.includes(s)} onClick={() => setAvailability(toggle(availability, s))} testId={`profile-availability-${s}`}>{s}</Chip>
                  ))}
                </div>
              </Field>
              <Field label="Transportation">
                <div className="flex flex-wrap gap-2">
                  {TRANSPORT_METHODS.map((s) => (
                    <Chip key={s} active={transport.includes(s)} onClick={() => setTransport(toggle(transport, s))} testId={`profile-transport-${s}`}>{s}</Chip>
                  ))}
                </div>
              </Field>
              <Button variant="secondary" onClick={setLocation} className="w-full" data-testid="profile-location-button">
                <MapPin className="h-4 w-4 mr-1" /> Use my current location
              </Button>
            </CardContent>
          </Card>

          {/* Personality */}
          <Card>
            <CardHeader><CardTitle className="bb-display">Style</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Comfortable encouraging athletes during workouts?">
                <Select value={encouragement} onValueChange={setEncouragement}>
                  <SelectTrigger data-testid="profile-encouragement"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {ENCOURAGEMENT.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Can count makes / stats during drills</div>
                  <div className="text-xs text-muted-foreground">Great for serious shooters.</div>
                </div>
                <Switch checked={canCount} onCheckedChange={setCanCount} data-testid="profile-count-stats" />
              </div>
            </CardContent>
          </Card>

          {/* Trust & safety */}
          <Card>
            <CardHeader>
              <CardTitle className="bb-display flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> Trust & safety
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Background check consent</div>
                  <div className="text-xs text-muted-foreground">Required for the "Background Checked" badge.</div>
                </div>
                <Switch checked={bgCheck} onCheckedChange={setBgCheck} data-testid="profile-bg-check" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Emergency contact name">
                  <Input value={ec.name || ""} onChange={(e) => setEc({ ...ec, name: e.target.value })} data-testid="profile-ec-name" />
                </Field>
                <Field label="Relationship">
                  <Input value={ec.relationship || ""} onChange={(e) => setEc({ ...ec, relationship: e.target.value })} placeholder="e.g. mother" data-testid="profile-ec-rel" />
                </Field>
              </div>
              <Field label="Emergency contact phone">
                <Input value={ec.phone || ""} onChange={(e) => setEc({ ...ec, phone: e.target.value })} placeholder="+1 555 123 4567" data-testid="profile-ec-phone" />
              </Field>
            </CardContent>
          </Card>

          {/* ID Verification */}
          <Card>
            <CardHeader>
              <CardTitle className="bb-display flex items-center justify-between">
                Government ID verification
                {verif && <StatusChip status={verif.verification_status} />}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {verif?.verification_status === "approved" ? (
                <InlineNotice tone="success" title="You're approved">
                  You can accept jobs and go online.
                  {verif?.match?.similarity != null && (
                    <span className="block text-xs mt-1 tabular-nums">Auto-verified at {verif.match.similarity}% face match</span>
                  )}
                </InlineNotice>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Upload a photo of a government-issued ID <strong>and a selfie</strong>. Our system runs an instant face-match — verified ball boys are auto-approved.
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Government ID</Label>
                      <input
                        type="file" accept="image/*"
                        onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setIdPhoto(await readFile(f)); }}
                        data-testid="ballboy-id-photo-input" className="block text-xs w-full"
                      />
                      {idPhoto && <img alt="ID preview" src={idPhoto} className="rounded-lg border border-border max-h-32 w-full object-contain" />}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Selfie</Label>
                      <input
                        type="file" accept="image/*" capture="user"
                        onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setSelfie(await readFile(f)); }}
                        data-testid="ballboy-selfie-input" className="block text-xs w-full"
                      />
                      {selfie && <img alt="Selfie preview" src={selfie} className="rounded-lg border border-border max-h-32 w-full object-contain" />}
                    </div>
                  </div>

                  <Button onClick={uploadId} className="w-full" disabled={!idPhoto || !selfie || verifying} data-testid="ballboy-id-upload-button">
                    <Upload className="h-4 w-4 mr-1" /> {verifying ? "Verifying…" : "Submit & verify"}
                  </Button>

                  {verif?.match?.similarity != null && (
                    <p className="text-xs text-muted-foreground" data-testid="ballboy-verif-similarity">
                      Last face match: <span className="font-semibold tabular-nums">{verif.match.similarity}%</span>
                      {verif.match.reason ? <span> · {verif.match.reason}</span> : null}
                    </p>
                  )}
                  {verif?.id_check?.flags?.length > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="ballboy-verif-flags">
                      ID flags: {verif.id_check.flags.join(", ")}
                    </p>
                  )}
                  {verif?.verification_status === "pending" && <InlineNotice tone="info" title="Under review">Your ID is pending review.</InlineNotice>}
                  {verif?.verification_status === "rejected" && <InlineNotice tone="destructive" title="Rejected">{verif.reason || "Please upload clearer photos."}</InlineNotice>}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Button onClick={save} className="w-full h-12" disabled={saving} data-testid="profile-save-button">
        {saving ? "Saving…" : (<><CheckCircle2 className="h-4 w-4 mr-2" /> Save profile</>)}
      </Button>

      <Button variant="ghost" className="w-full text-destructive" onClick={logout} data-testid="logout-button">
        <LogOut className="h-4 w-4 mr-1" /> Log out
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs rounded-full border transition-colors capitalize ${
        active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"
      }`}
    >
      {children}
    </button>
  );
}
